import { useState, useEffect } from 'react';
import { subscribe as subscribeToEvent, unsubscribe as unsubscribeFromEvent, dispatchEvent } from '../../shared/events';
import salesEvents from '../../shared/salesEvents';
import { track } from './backend';
import { getNextPlanForLimits, getLimitsForTier, getUserTier } from '../../shared/plans';

const signupModalEvents = [
  "clickedExport",
  "requestedBulkSearch",
  "clickedSaveToList",
  "clickedLockedCountry",
  "clickedGSC",
  "clickedSeeMoreKeywords",
  "clickedGetMoreCompetitors",
  "clickedCreateRankingList",
  "clickedUnlock",
];

export default function useUpsellEvents(user) {

  const [Funnel, SetFunnel] = useState(null);
  const [PriceId, SetPriceId] = useState(null);
  const [LastUserAction, SetLastUserAction] = useState(null);

  const activate = (funnel, priceId, lastUserAction) => {
    SetFunnel(funnel);
    SetPriceId(priceId);
    SetLastUserAction(lastUserAction);
  };

  const onShowSignupModal = (e) => {
    if (user.coupon) return onShowRedeemCoupon();
    activate('subscribe', 'bronze_trial_plan', e.type);
  };

  const onRequestsExhausted = () => {
    const keyword = new URLSearchParams(window.location.search).get('query');
    const tool = window.location.pathname.replace('/', '');
    track('paywall_reached', { keyword, searches_remaining: 0, tool });
    activate('paywall_reached', 'bronze_trial_plan');
  };

  const onExtraRequestsExhausted = () => {
    const keyword = new URLSearchParams(window.location.search).get('query');
    const tool = window.location.pathname.replace('/', '');
    track('paywall_reached', { keyword, searches_remaining: 0, tool });
    activate('paywall_reached', 'bronze_trial_plan');
  };

  const onRequestBlockedByExpiredSubscription = () => {
    const funnel = user.hasValidCard ? 'expired_with_valid_card' : 'expired_with_invalid_card';
    activate(funnel, 'bronze_monthly');
  };

  const onTriedToExceedDailySearches = () => {
    const currentDaily = getLimitsForTier(getUserTier(user)).searches_per_day;
    const priceId = getNextPlanForLimits(user, 'searches_per_day', currentDaily + 1);
    activate('out_of_daily_searches', priceId);
  };

  const onTriedToExceedRankingLists = e => {
    const { requested } = e.detail;
    const priceId = getNextPlanForLimits(user, 'ranking_lists', requested);
    const funnel = priceId ? 'out_of_ranking_lists' : 'curious_about_even_more_ranking_lists';
    activate(funnel, priceId);
  };

  const onBrowsingRankingLists = e => {
    const { requested } = e.detail;
    const priceId = getNextPlanForLimits(user, 'ranking_lists', requested);
    const funnel = priceId ? 'curious_about_ranking_lists' : 'curious_about_even_more_ranking_lists';
    activate(funnel, priceId);
  };

  const onRequestedSubscribe = () => {
    activate('subscribe', 'bronze_trial_plan');
  };

  const onShowRedeemCoupon = () => {
    activate('coupon', 'bronze_trial_plan');
  };

  const eventHandlers = [
    ["requestBlockedByExpiredSubscription", onRequestBlockedByExpiredSubscription],
    ["triedToExceedRankingLists", onTriedToExceedRankingLists],
    ["triedToExceedDailySearches", onTriedToExceedDailySearches],
    ["browsingRankingLists", onBrowsingRankingLists],
    ["requestedSubscribe", onRequestedSubscribe],
    ["requestsExhausted", onRequestsExhausted],
    ["extraRequestsExhausted", onExtraRequestsExhausted],
    ["showRedeemCoupon", onShowRedeemCoupon],
    ...signupModalEvents.map(e => [e, onShowSignupModal]),
  ];

  useEffect(() => {
    eventHandlers.forEach(([event, handler]) => subscribeToEvent(event, handler));
    return () => {
      eventHandlers.forEach(([event, handler]) => unsubscribeFromEvent(event, handler));
    };
  }, []);

  useEffect(() => {
    salesEvents.add('funnel', Funnel || 'closed', { PriceId, LastUserAction });
  }, [Funnel]);

  const reset = () => { SetFunnel(null); SetPriceId(null); SetLastUserAction(null); };

  return { Funnel, PriceId, LastUserAction, reset };
}
